import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';

/**
 * Unified date helpers.
 *
 * Date-only fields (Prisma `@db.Date`) are serialised by the backend as
 * UTC-midnight ISO strings ("2026-08-15T00:00:00.000Z"). Formatting such a
 * value with the viewer's local timezone shifts the calendar day for users
 * west of UTC. The date-only helpers below therefore format in UTC so the
 * stored calendar day renders identically for every viewer.
 *
 * Real timestamps (createdAt, audit times, `@db.Timestamptz`) must instead be
 * shown in the viewer's local timezone — use {@link formatDateTime}.
 */

export type DateInput = string | Date | null | undefined;

/** Named presets for date-only display, mirroring the formats already in use. */
export type DateStyle = 'short' | 'long' | 'dayMonth' | 'monthDay' | 'monthYear';

const DATE_ONLY_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { month: 'short', day: 'numeric', year: 'numeric' }, // Jun 22, 2026
  long: { month: 'long', day: 'numeric', year: 'numeric' }, // June 22, 2026
  dayMonth: { day: 'numeric', month: 'long', year: 'numeric' }, // 22 June 2026
  monthDay: { month: 'short', day: 'numeric' }, // Jun 22
  monthYear: { month: 'short', year: 'numeric' }, // Jun 2026
};

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date-only value ("YYYY-MM-DD" or UTC-midnight ISO) for display.
 * Rendered in UTC so the stored calendar day is identical for every viewer.
 * Returns '' for empty/invalid input — callers add their own placeholder.
 * Pass `locale` to control locale-specific field ordering (default en-US).
 */
export function formatDate(
  value: DateInput,
  style: DateStyle | Intl.DateTimeFormatOptions = 'short',
  locale = 'en-US'
): string {
  const date = toDate(value);
  if (!date) return '';
  const options = typeof style === 'string' ? DATE_ONLY_STYLES[style] : style;
  return date.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

/**
 * Format a date-only range. Collapses to a single date when both ends fall on
 * the same calendar day.
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  style: DateStyle = 'short'
): string {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate && !endDate) return '';
  if (!startDate) return formatDate(endDate, style);
  if (!endDate || toDateKey(startDate) === toDateKey(endDate)) return formatDate(startDate, style);
  // formatRange collapses same-month ranges ("Mar 25 – 26, 2026") and repeats the
  // month/year only where it differs, in UTC so the calendar day is stable for all viewers.
  return new Intl.DateTimeFormat('en-US', {
    ...DATE_ONLY_STYLES[style],
    timeZone: 'UTC',
  }).formatRange(startDate, endDate);
}

/**
 * Format a real timestamp in the viewer's local timezone. Defaults to a medium
 * date with short time ("Jun 22, 2026, 3:45 PM"); pass options to override.
 */
export function formatDateTime(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }
): string {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleString('en-US', options);
}

/**
 * Calendar-date key ("YYYY-MM-DD") of a backend date-only value. Takes the UTC
 * calendar day, matching how `@db.Date` values are serialised. Use for
 * comparing/sorting date-only values, never for display.
 */
export function toDateKey(value: DateInput): string {
  const date = toDate(value);
  if (!date) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calendar-date key ("YYYY-MM-DD") of a *timestamp*, in the viewer's own
 * timezone — the day they would say it is.
 *
 * Distinct from `toDateKey`, which reads the UTC day because backend date-only
 * values are serialised at UTC midnight. Use this one for anything derived from
 * a real instant that a person also sees on screen: east of UTC, a local
 * midnight is the previous day in UTC, so taking the UTC day there labels a
 * period a day earlier than the screen says it is.
 */
export function toLocalDayKey(value: DateInput): string {
  const date = toDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a date-only value ("YYYY-MM-DD" or UTC-midnight ISO) into a local-midnight
 * `Date`, for date arithmetic and calendar positioning (not display). Avoids the
 * UTC-parsing drift of `new Date("YYYY-MM-DD")` in timezones west of UTC.
 */
export function parseDateOnly(value: string): Date {
  return parseISO(value.slice(0, 10));
}

/**
 * Serialise a picker `Date` (DatePicker/Calendar returns one at LOCAL midnight) to a
 * date-only "YYYY-MM-DD" string for the backend's `@db.Date` fields. Takes the LOCAL
 * calendar day: `toISOString()` converts to UTC and, for viewers east of UTC, rolls
 * the day back one (e.g. picking Aug 12 in GMT+3 → "2026-08-11"). The backend parses
 * "YYYY-MM-DD" as UTC-midnight, so the intended day is stored and reads back identically
 * via {@link toDateKey}/{@link formatDate}. Returns null for empty/invalid input.
 *
 * The local-getter mirror of {@link toDateKey}: `toDateKey` reads the UTC day of a value
 * the backend already stored; `toDateParam` writes the local day the user just picked.
 * Only for date-only fields — real timestamps (`@db.Timestamptz`) must keep `toISOString()`.
 */
export function toDateParam(value: DateInput): string | null {
  const date = toDate(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * True when a date-only value falls strictly before the viewer's local today.
 * Both operands are reduced to a local calendar day (via {@link parseDateOnly}),
 * so an item dated today is never "past" in any timezone. Use for overdue
 * styling — comparing a UTC-midnight `@db.Date` value against a raw `new Date()`
 * tints a due-today item red west of UTC.
 */
export function isPastDateOnly(value: string | null | undefined): boolean {
  if (!value) return false;
  const date = parseDateOnly(value);
  if (Number.isNaN(date.getTime())) return false;
  return date < startOfDay(new Date());
}

/**
 * Whole calendar days from the viewer's local today to a date-only value:
 * negative when already past, 0 when today. Returns null for empty/invalid input.
 * Calendar-day based, so the count is identical in every timezone.
 */
export function daysUntilDateOnly(value: string | null | undefined): number | null {
  if (!value) return null;
  const date = parseDateOnly(value);
  if (Number.isNaN(date.getTime())) return null;
  return differenceInCalendarDays(date, startOfDay(new Date()));
}

/**
 * Whole calendar days spanned by two date-only values (`end` − `start`).
 * Returns null when either value is empty/invalid.
 */
export function daysBetweenDateOnly(
  start: string | null | undefined,
  end: string | null | undefined
): number | null {
  if (!start || !end) return null;
  const startDate = parseDateOnly(start);
  const endDate = parseDateOnly(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  return differenceInCalendarDays(endDate, startDate);
}
